import { useMemo, useState } from 'react';

import {
  ArrowRight,
  BellRing,
  MoreHorizontal,
  PlusCircle,
  Star,
  UsersRound
} from 'lucide-react';
import { motion } from 'motion/react';
import pathToRegexp from 'path-to-regexp';
import { Link } from 'react-router-dom';

import { Button } from '@/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/shadcn/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/shadcn/dropdown-menu';
import { cn } from '@/shadcn/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/shadcn/tooltip';

import { Ellipsis } from '@/components/ui/ellipsis/ellipsis';
import {
  ROUTE_WORKFLOW_GROUPS,
  ROUTE_WORKFLOW_OVERVIEW
} from '@/constant/routes';
import { formatNumber } from '@/utils';
import SubscriptionsMenu from '../workflowsList/row/subscriptionsMenu';
import UploadDocumentationModal from '../workflowsList/row/uploadDocumentationModal/uploadDocumentationModal';
import { WorkflowDocButton } from './WorkflowDocButton';
import { WorkspaceVersionBadge } from './WorkspaceVersionBadge';
import { isSubscriptionsDisabled } from './workflowCards.schema';
import type { WorkflowCardItem } from './workspaceFilterMatch';

type Props = {
  workflow: WorkflowCardItem;
  isPrivileged: boolean;
  userId: string;
  isFavorite: boolean;
  onToggleFavorite: (rowId: string, value: boolean) => void;
};

// Arrow reveal on card hover: the slot grows from 0 width (no reserved space at
// rest) which pushes the "Open" text left and widens the button; the icon slides
// in from the right. Spring (non-linear), no bounce.
const ARROW_SLOT_VARIANTS = {
  rest: { width: 0, marginLeft: 0 },
  hover: { width: 14, marginLeft: 4 }
};

const ARROW_ICON_VARIANTS = {
  rest: { opacity: 0, x: 8 },
  hover: { opacity: 1, x: 0 }
};

const ARROW_TRANSITION = { type: 'spring', duration: 0.35, bounce: 0 } as const;

export const WorkflowCard = ({
  workflow,
  isPrivileged,
  userId,
  isFavorite,
  onToggleFavorite
}: Props) => {
  const { rowId, name, canUpdate, workspace, settings, documentation } =
    workflow;
  const actions = workflow.actions?.nodes ?? [];
  const tracesCount = workflow.traces?.totalCount ?? 0;
  const groupsCount = workflow.groups?.totalCount ?? 0;
  const notificationsEnabled = !isSubscriptionsDisabled(settings);
  const canManageGroups = Boolean(canUpdate) && isPrivileged;
  const showMenu = Boolean(canUpdate);
  const hasDocumentation = Boolean(documentation?.url || documentation?.file);
  const favoriteLabel = isFavorite
    ? 'Remove from favorites'
    : 'Add to favorites';

  const [docDialogOpen, setDocDialogOpen] = useState(false);

  const overviewPath = useMemo(
    () => pathToRegexp.compile(ROUTE_WORKFLOW_OVERVIEW)({ id: rowId }),
    [rowId]
  );
  const groupsPath = useMemo(
    () => pathToRegexp.compile(ROUTE_WORKFLOW_GROUPS)({ id: rowId }),
    [rowId]
  );

  return (
    <>
      <motion.div
        animate="rest"
        className="group relative flex h-full min-h-40 flex-col gap-1.5 rounded-lg border border-border bg-card p-4 text-card-foreground"
        data-cy={`workflow-card-${rowId}`}
        initial="rest"
        whileHover="hover"
      >
        <div className="flex items-center justify-between gap-2">
          <Link
            className="min-w-0 font-semibold text-card-foreground text-sm no-underline after:absolute after:inset-0 after:content-['']"
            data-cy={`workflow-card-name-${rowId}`}
            to={overviewPath}
          >
            <Ellipsis className="capitalize">{name}</Ellipsis>
          </Link>

          <TooltipProvider>
            <div className="relative z-10 flex shrink-0 items-center gap-0.5">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    aria-label={favoriteLabel}
                    className="group/fav"
                    data-cy={`workflow-card-favorite-${rowId}`}
                    onClick={() => onToggleFavorite(rowId, !isFavorite)}
                    size="icon"
                    variant="ghost"
                  >
                    <Star
                      className={cn(
                        'text-foreground',
                        isFavorite && 'fill-yellow-500 text-yellow-500'
                      )}
                    />
                  </Button>
                </TooltipTrigger>
                <TooltipContent variant="contrast">
                  {favoriteLabel}
                </TooltipContent>
              </Tooltip>

              {notificationsEnabled && (
                <Dialog>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DialogTrigger asChild>
                        <Button
                          aria-label="Manage notification subscriptions"
                          data-cy="subscriptions-button"
                          data-testid={`workflow-card-notifications-${rowId}`}
                          size="icon"
                          variant="ghost"
                        >
                          <BellRing />
                        </Button>
                      </DialogTrigger>
                    </TooltipTrigger>
                    <TooltipContent variant="contrast">
                      Manage notification subscriptions
                    </TooltipContent>
                  </Tooltip>
                  <DialogContent>
                    <SubscriptionsMenu
                      actions={actions}
                      userId={userId}
                      workflowId={rowId}
                    />
                  </DialogContent>
                </Dialog>
              )}

              {showMenu && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      aria-label="More actions"
                      data-testid={`workflow-card-menu-${rowId}`}
                      size="icon"
                      variant="ghost"
                    >
                      <MoreHorizontal />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {canUpdate && (
                      <DropdownMenuItem
                        data-testid={`workflow-card-add-doc-${rowId}`}
                        onSelect={() => setDocDialogOpen(true)}
                      >
                        <PlusCircle />
                        Add documentation
                      </DropdownMenuItem>
                    )}
                    {canManageGroups && (
                      <DropdownMenuItem
                        asChild
                        data-testid={`workflow-card-groups-${rowId}`}
                      >
                        <Link to={groupsPath}>
                          <UsersRound />
                          Manage groups
                        </Link>
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </TooltipProvider>
        </div>

        {workspace && (
          <div className="flex min-w-0 items-center gap-1.5">
            <span
              aria-hidden
              className="size-2.5 shrink-0 rounded-full"
              data-testid="workflow-card-workspace-dot"
              style={{ backgroundColor: workspace.color }}
            />
            <Ellipsis className="text-muted-foreground text-xs">
              {workspace.name}
            </Ellipsis>
            <WorkspaceVersionBadge
              className="relative z-10"
              version={workspace.version}
            />
          </div>
        )}

        <div className="mt-auto flex items-center justify-between gap-2">
          <div className="flex items-center gap-1 text-muted-foreground text-xs">
            <span
              className="font-medium text-card-foreground tabular-nums"
              data-cy="trace-count"
            >
              {formatNumber(tracesCount)}
            </span>
            <span>traces</span>
            <span aria-hidden className="text-muted-foreground/50">
              ·
            </span>
            <span
              className="font-medium text-card-foreground tabular-nums"
              data-cy="group-count"
            >
              {formatNumber(groupsCount)}
            </span>
            <span>groups</span>
          </div>

          <div className="flex items-center gap-1.5">
            {hasDocumentation && documentation && (
              <WorkflowDocButton documentation={documentation} rowId={rowId} />
            )}
            <Link
              className="relative z-10 inline-flex items-center rounded-md border border-border bg-card px-3 py-1.5 font-medium text-foreground text-xs no-underline"
              data-testid={`workflow-card-open-${rowId}`}
              to={overviewPath}
            >
              Open
              <motion.span
                aria-hidden
                className="flex items-center overflow-hidden"
                transition={ARROW_TRANSITION}
                variants={ARROW_SLOT_VARIANTS}
              >
                <motion.span
                  className="flex"
                  transition={ARROW_TRANSITION}
                  variants={ARROW_ICON_VARIANTS}
                >
                  <ArrowRight className="shrink-0" size={14} />
                </motion.span>
              </motion.span>
            </Link>
          </div>
        </div>
      </motion.div>

      {canUpdate && (
        <Dialog onOpenChange={setDocDialogOpen} open={docDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add documentation</DialogTitle>
            </DialogHeader>
            <UploadDocumentationModal workflowRowId={rowId} />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
